import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { ApiHttpService } from './api-http.service';
import { UrlHelper } from './url-helper';
import { QueryStringParameters } from './query-string-parameters';
import { ApiRoutes } from '../shared/apiroutes';
import { AuthService } from './api-endpoints.service';
// import Logging from './Logging';
import { IBooking, ICity, IUser } from '../../app/booking/model';
import { IApiResponse, IPagination } from '../library/interfaces';
// import { IApiResponse, IPagination } from '../../../../shared/src/import-export';

@Injectable({ providedIn: 'root' })
export class CrudManagementService {
  apiHttpService = inject(ApiHttpService);
  authService = inject(AuthService);

  getEventValue = (event: Event): string => (event.target as HTMLInputElement).value;

  get = (
    url: string,
    queryStringHandler?: (queryStringParameters: QueryStringParameters) => void,
    options?: any
  ) => this.apiHttpService
    .get(UrlHelper.createUrlWithQueryParameters(url, queryStringHandler), options)

  post = (url: string, payload: any, pathVariables?: any[], options?: any) => this.apiHttpService
    .post(UrlHelper.createUrlWithPathVariables(url, pathVariables), payload, options)

  patch = (url: string, payload: any, pathVariables?: any[]) => this.apiHttpService
    .patch(UrlHelper.createUrlWithPathVariables(url, pathVariables), payload)

  delete = (url: string, pathVariables?: any[]) => this.apiHttpService
    .delete(UrlHelper.createUrlWithPathVariables(url, pathVariables));

  private getMany<T>(apiRoute: string, dataList: WritableSignal<T[]>, logMessage: string) {
    return new Promise<void>((resolve, reject) => {
      this.get(apiRoute)
        .subscribe({
          next: (value) => {
            // Logging.info(logMessage, value);
            const paginatedResponse = value as IApiResponse<T[]>;
            dataList.set(!paginatedResponse.error ? paginatedResponse.data : []);
            resolve()
          },
          error: (error) => {
            // Logging.error(`${logMessage} error`, error);
            resolve()
          },
          complete: () => { },
        })
        .add(() => { });
    })

  }

  private getPaginatedMany<T>(apiRoute: string, dataList: WritableSignal<T>, logMessage: string, itemsPerPage: number = this.authService.authState().orgId?.paginatedItemsPerPage ?? 10, currentPage: number = 1, queryParams?: Record<string, any>) {
    return new Promise<T>((resolve, reject) => {
      this
        .get(apiRoute, (qp) => {
          if (queryParams) {
            for (const [key, value] of Object.entries(queryParams)) {
              qp.push(key, value);
            }
          }
          // if (this.searchText) qp.push('searchText', this.searchText);
          // if (this.selectedCompany) qp.push('companyId', this.selectedCompany);
          // if (this.selectedVendor) qp.push('vendorId', this.selectedVendor);
          qp.push('itemsPerPage', itemsPerPage);
          qp.push('currentPage', currentPage);
        })
        .subscribe({
          next: (value) => {
            // Logging.info(logMessage, value);
            const paginatedResponse = value as IApiResponse<T>;
            if (!paginatedResponse.error) {
              dataList.set(paginatedResponse.data);
              resolve(paginatedResponse.data)
            } else {
              reject(null)
            }
          },
          error: (error) => {
            // Logging.error(`${logMessage} error`, error);
            reject(error)
          },
          complete: () => { },
        })
    })

  }


  userList = signal<IUser[]>([]);
  getUsers = () => this.getMany<IUser>(ApiRoutes.user, this.userList, 'getUsers');
  userPaginatedList = signal<IPagination<IUser>>(undefined);
  getUsersPaginated = (itemsPerPage: number = this.authService.authState()?.paginatedItemsPerPage ?? 10, currentPage: number = 1, queryParams?: Record<string, any>) => this.getPaginatedMany<IPagination<IUser>>(ApiRoutes.user, this.userPaginatedList, 'getUsersPaginated', itemsPerPage, currentPage, queryParams);

  bookingList = signal<IBooking[]>([]);
  getbookings = () => this.getMany<IBooking>(ApiRoutes.bookings, this.bookingList, 'getBookings');
  bookingPaginatedList = signal<IPagination<IBooking>>(undefined);
  getBookingPaginated = (itemsPerPage: number = this.authService.authState()?.paginatedItemsPerPage ?? 10, currentPage: number = 1, queryParams?: Record<string, any>) => this.getPaginatedMany<IPagination<IBooking>>(ApiRoutes.bookings, this.bookingPaginatedList, 'getBookingPaginated', itemsPerPage, currentPage, queryParams);

  cityList = signal<ICity[]>([]);
  getcity = () => this.getMany<ICity>(ApiRoutes.city, this.cityList, 'getcity');
  cityPaginatedList = signal<IPagination<ICity>>(undefined);
  getCityPaginated = (itemsPerPage: number = this.authService.authState()?.paginatedItemsPerPage ?? 10, currentPage: number = 1, queryParams?: Record<string, any>) => this.getPaginatedMany<IPagination<ICity>>(ApiRoutes.city, this.cityPaginatedList, 'getCityPaginated', itemsPerPage, currentPage, queryParams);

  private deleteOne(apiRoute: string, id: any, logMessage: string, cb?: () => void) {
    this
      .delete(apiRoute, [id])
      .subscribe({
        next: (value) => {
          // Logging.info(logMessage, value);
          const res = value as IApiResponse<any>;
          if (!res.error) {
            cb()
          }
        },
        error: (error) => {
          // Logging.error(`${logMessage} error`, error);
        },
        complete: () => { },
      })
  }

  deleteAccount = (id: any, cb?: () => void) => this.deleteOne(ApiRoutes.user, id, 'deleteAccount', cb);
  deleteBooking = (id: any, cb?: () => void) => this.deleteOne(ApiRoutes.bookings, id, 'deleteBooking', cb);
  deleteCity = (id: any, cb?: () => void) => this.deleteOne(ApiRoutes.city, id, 'deleteCity', cb);

  getOne<T>(apiRoute: string, id: any, logMessage: string, cb: (data: T) => void) {
    this
      .get(ApiRoutes.byId(apiRoute, id))
      .subscribe({
        next: (value) => {
          // Logging.info(logMessage, value);
          const res = value as IApiResponse<T>;
          if (!res.error) {
            cb(res.data)
          }
        },
        error: (error) => {
          // Logging.error(`${logMessage} error`, error);
        },
        complete: () => { },
      })
  }

  getAccount = (id: any, cb: (data: IUser) => void) => this.getOne<IUser>(ApiRoutes.user, id, 'getAccount', cb);
  getBooking = (id: any, cb: (data: IBooking) => void) => this.getOne<IBooking>(ApiRoutes.bookings, id, 'getBooking', cb);
  getSingleBooking = (id: any, cb: (data: IBooking) => void) => this.getOne<IBooking>(ApiRoutes.singleBookings, id, 'getSingleBooking', cb);
  getCity = (id: any, cb: (data: ICity) => void) => this.getOne<ICity>(ApiRoutes.city, id, 'getCity', cb);

  addEditOne(apiRoute: string, object: any, logMessage: string, cb: (data: boolean) => any, id?: any, vcb?: (data: any) => any) {
    const observer = id
      ? this.patch(apiRoute, object, [id])
      : this.post(apiRoute, object);
    observer
      .subscribe({
        next: (value: any) => {
          // Logging.info(logMessage, value);
          if (!value.error) {
            cb(!id)
            if (vcb != undefined && value.data) {
              vcb(value.data)
            }
          };
        },
        error: (error) => {
          // Logging.error(`${logMessage} error`, error);
        },
        complete: () => { },
      })
  }

}
